import { env } from '@shared/config/env'
import { normalizeLocale } from '@shared/config/i18n/resolve'
import { z } from 'zod'

export const termsCountrySchema = z.enum(['KR', 'JP'])
export type TermsCountry = z.infer<typeof termsCountrySchema>

/** Resolved account country is authoritative: JP uses JP, all others KR; missing profile never uses language. */
export function selectTermsCountry({
  isAuthorized,
  countryCode,
  languageCode,
  systemLocale,
}: {
  isAuthorized: boolean
  countryCode?: string
  languageCode: string
  systemLocale: string | null
}): TermsCountry | null {
  if (isAuthorized) {
    if (!countryCode?.trim()) {
      return null
    }
    return countryCode === 'JP' ? 'JP' : 'KR'
  }
  const language = languageCode === 'system' ? systemLocale?.split(/[;,]/)[0] : languageCode
  return normalizeLocale(language) === 'ja' ? 'JP' : 'KR'
}

const publicTermsResponseSchema = z.object({
  resultCd: z.literal(200),
  data: z.object({
    id: z.number().int().positive(),
    site: z.enum(['PODO', 'PODO_JP']),
    type: z.literal('SERVICE'),
    state: z.literal('NORMAL'),
    title: z.string().trim().min(1),
    content: z.string().trim().min(1),
  }),
})

/** Same public endpoint as marketing's /.api/info/service rewrite. No session or user URL is forwarded. */
export async function fetchPublicPaymentTerms(country: TermsCountry, signal?: AbortSignal) {
  const url = new URL('/api/v2/marketing/info/service', env.NEXT_PUBLIC_PODO_API_URL)
  if (country === 'JP') {
    url.searchParams.set('locale', 'ja')
  }
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  const timeout = setTimeout(onAbort, 10_000)
  signal?.addEventListener('abort', onAbort, { once: true })
  if (signal?.aborted) {
    controller.abort()
  }
  try {
    const response = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal })
    if (!response.ok) {
      throw new Error('Failed to load public terms')
    }
    const { data } = publicTermsResponseSchema.parse(await response.json())
    if (data.site !== (country === 'JP' ? 'PODO_JP' : 'PODO')) {
      throw new Error('Unexpected public terms country')
    }
    return data
  } finally {
    clearTimeout(timeout)
    signal?.removeEventListener('abort', onAbort)
  }
}
